import { h as create, Fragment, h, VNode } from "preact";
import {
  ErrorAndLabel,
  FormErrors,
  FormModel,
  useForm,
} from "../hooks/useForm.js";
// import { getConverterById, useTranslationContext } from "../index.browser.js";
import { useState } from "preact/hooks";
import { useTranslationContext } from "../context/translation.js";
import {
  FieldComponentFunction,
  UIFormConfiguration,
  UIFormField,
} from "./field-types.js";
import {
  DoubleColumnFormSection,
  FormDesign,
  UIFormElementConfig,
} from "./forms-types.js";
import { convertFormConfigToUiField } from "./forms-utils.js";
import { AcceptTosForm, AcceptTosFormValues } from "./AcceptTosForm.js";
import { Attention } from "../components/Attention.js";
import { FormLanguagePolicy } from "./forms-types.js";

export type FormLanguageStatus =
  | "legally-binding"
  | "official-non-binding"
  | "unofficial";

export function getFormLanguageStatus(
  policy: FormLanguagePolicy,
  language: string,
): FormLanguageStatus {
  const baseLanguage = language
    .replace("_", "-")
    .toLowerCase()
    .split("-", 1)[0];
  const hasLanguage = (candidate: string): boolean =>
    candidate.replace("_", "-").toLowerCase().split("-", 1)[0] === baseLanguage;
  if (policy.legallyBindingLanguages.some(hasLanguage)) {
    return "legally-binding";
  }
  if (policy.officialLanguages.some(hasLanguage)) {
    return "official-non-binding";
  }
  return "unofficial";
}

export function FormLanguageNotice({
  policy,
}: {
  policy: FormLanguagePolicy | undefined;
}): VNode | null {
  const { i18n, lang } = useTranslationContext();
  if (!policy) return null;
  const status = getFormLanguageStatus(policy, lang);
  switch (status) {
    case "legally-binding":
      return (
        <Attention
          type="info"
          title={i18n.fixed({
            en: "Official form language",
            de: "Offizielle Formularsprache",
          })}
        >
          {i18n.fixed({
            en: "This form is shown in an official, legally binding language.",
            de: "Dieses Formular wird in einer offiziellen, rechtsverbindlichen Sprache angezeigt.",
          })}
        </Attention>
      );
    case "official-non-binding":
      return (
        <Attention
          type="warning"
          title={i18n.fixed({
            en: "Official translation",
            de: "Offizielle Übersetzung",
          })}
        >
          {i18n.fixed(
            {
              en: "This form is shown in an official translation, but it is not legally binding. Only the %1$s text is legally binding.",
              de: "Dieses Formular wird in einer offiziellen Übersetzung angezeigt, die jedoch nicht rechtsverbindlich ist. Nur der Text in %1$s ist rechtsverbindlich.",
            },
            policy.legallyBindingLanguageLabel,
          )}
        </Attention>
      );
    case "unofficial":
      return (
        <Attention
          type="warning"
          title={i18n.fixed({
            en: "Unofficial form translation",
            de: "Inoffizielle Formularübersetzung",
          })}
        >
          {i18n.fixed(
            {
              en: "This form is not officially supported in the selected language. The translation may be incomplete, and some text may be shown in %1$s. Only the %2$s text is legally binding.",
              de: "Dieses Formular wird in der ausgewählten Sprache nicht offiziell unterstützt. Die Übersetzung kann unvollständig sein, und Teile des Textes können in %1$s angezeigt werden. Nur der Text in %2$s ist rechtsverbindlich.",
            },
            policy.fallbackLanguageLabel,
            policy.legallyBindingLanguageLabel,
          )}
        </Attention>
      );
  }
}

export function DefaultForm<T>({
  design,
  initial,
  disabled,
}: {
  disabled?: boolean;
  design: FormDesign;
  initial: object;
}): VNode {
  const { model: handler, status } = useForm<T>(design, initial as any);

  const [shorten, setShorten] = useState(true);

  return (
    <div>
      <FormUI design={design} model={handler} disabled={disabled} />
      <hr class="mt-3 mb-3" />
      <label>
        <input
          type="checkbox"
          checked={shorten}
          onChange={(e) => setShorten(!shorten)}
        />{" "}
        Shorten file contents.
      </label>

      <p>Result JSON:</p>
      <pre class="break-all whitespace-pre-wrap">
        {JSON.stringify(
          shorten
            ? redactFileContents(status.result ?? {})
            : (status.result ?? {}),
          undefined,
          2,
        )}
      </pre>
      <hr class="mt-3 mb-3" />
      {status.status !== "ok" && design.type !== "accept-tos" ? (
        <ErrorsSummary errors={status.errors} />
      ) : undefined}
    </div>
  );
}

export function redactFileContents(result: unknown): unknown {
  if (Array.isArray(result)) {
    return result.map((x) => redactFileContents(x));
  }
  if (result === null || typeof result !== "object") return result;
  if (
    "ENCODING" in result &&
    result.ENCODING === "base64" &&
    "CONTENTS" in result
  ) {
    return {
      ...result,
      CONTENTS: "[... skipped ...]",
    };
  }
  return Object.fromEntries(
    Object.entries(result).map(([k, v]) => {
      return [k, redactFileContents(v)];
    }),
  );
}

export const DEFAULT_FORM_UI_NAME = "form-ui";

/**
 * FIXME: formDesign should be embedded in formHandler
 */
export function FormUI<T>({
  name = DEFAULT_FORM_UI_NAME,
  design,
  model,
  disabled,
  focus,
  onSubmit,
}: {
  name?: string;
  design: FormDesign;
  model: FormModel<T>;
  focus?: boolean;
  disabled?: boolean;
  onSubmit?: () => void;
}): VNode {
  const languageNotice = <FormLanguageNotice policy={design.languagePolicy} />;
  switch (design.type) {
    case "accept-tos": {
      return (
        <Fragment>
          {languageNotice}
          <AcceptTosForm
            name={name}
            design={design}
            model={model as unknown as FormModel<AcceptTosFormValues>}
            focus={focus}
            onSubmit={onSubmit}
            disabled={disabled}
          />
        </Fragment>
      );
    }
    case "double-column": {
      const ui = design.sections.map((section, i) => {
        if (!section) return <Fragment key={i} />;
        return (
          <DoubleColumnFormSectionUI
            sectionKey={`${i}`}
            key={i}
            name={name}
            section={section}
            model={model}
            focus={focus}
            disabled={disabled}
            onSubmit={onSubmit}
          />
        );
      });
      return (
        <Fragment>
          {languageNotice}
          {design.title ? (
            <h1 class="text-lg font-bold">{design.title}</h1>
          ) : (
            <Fragment />
          )}
          {ui}
        </Fragment>
      );
    }
    case "single-column": {
      return (
        <Fragment>
          {languageNotice}
          <SingleColumnFormSectionUI
            name={name}
            fields={design.fields}
            model={model}
            focus={focus}
            onSubmit={onSubmit}
            disabled={disabled}
          />
        </Fragment>
      );
    }
  }
}

export function DoubleColumnFormSectionUI<T>({
  sectionKey,
  section,
  name,
  focus,
  model,
  disabled,
  onSubmit,
}: {
  sectionKey: string;
  name: string;
  model: FormModel<T>;
  section: DoubleColumnFormSection;
  focus?: boolean;
  disabled?: boolean;
  onSubmit?: () => void;
}): VNode {
  const { i18n } = useTranslationContext();
  const fs = convertFormConfigToUiField(
    i18n,
    sectionKey,
    section.fields,
    model,
  );
  const allHidden = fs.every((v) => {
    // FIXME: Handler should probably be present for all Form UI fields, not just some.
    if ("handler" in v.properties) {
      return v.properties.hidden ?? false;
    }
    return false;
  });
  const sectionHidden = model.isSectionHidden(sectionKey);
  if (allHidden || sectionHidden) {
    return (
      <RenderAllFieldsByUiConfig
        hidden={true}
        key="fields"
        fields={fs}
        focus={focus}
        disabled={disabled}
      />
    );
  }
  return (
    <form
      name={name}
      class="grid grid-cols-1 gap-x-8 gap-y-8 pt-5 md:grid-cols-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (onSubmit) onSubmit();
      }}
    >
      {onSubmit ? (
        <button
          type="submit"
          class="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        />
      ) : undefined}
      <div class="px-4 sm:px-0">
        <h2 class="text-base font-semibold leading-7 text-gray-900">
          {section.title}
        </h2>
        {section.description && (
          <p class="mt-1 text-sm leading-6 text-gray-600">
            {section.description}
          </p>
        )}
      </div>
      <div class="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-md md:col-span-2">
        <div class="p-3">
          <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <RenderAllFieldsByUiConfig
              key="fields"
              fields={fs}
              focus={focus}
              disabled={disabled}
            />
          </div>
        </div>
      </div>
    </form>
  );
}

export function SingleColumnFormSectionUI<T>({
  fields,
  name,
  model,
  focus,
  disabled,
  onSubmit,
}: {
  name: string;
  model: FormModel<T>;
  fields: UIFormElementConfig[];
  focus?: boolean;
  disabled?: boolean;
  onSubmit?: () => void;
}): VNode {
  const { i18n } = useTranslationContext();
  return (
    <form
      name={name}
      class="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-md md:col-span-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (onSubmit) onSubmit();
      }}
    >
      {onSubmit ? (
        <button
          type="submit"
          class="sr-only"
          tabIndex={-1}
          aria-hidden="true"
        />
      ) : undefined}
      <div class="p-3">
        <div class="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
          <RenderAllFieldsByUiConfig
            fields={convertFormConfigToUiField(i18n, `root`, fields, model)}
            focus={focus}
            disabled={disabled}
          />
        </div>
      </div>
    </form>
  );
}

export function RenderAllFieldsByUiConfig({
  focus,
  fields,
  hidden,
  disabled,
}: {
  fields: UIFormField[];
  focus?: boolean;
  disabled?: boolean;
  hidden?: boolean;
}): VNode {
  return create(
    Fragment,
    {},
    fields.map((field, i) => {
      const Component = UIFormConfiguration[
        field.type
      ] as FieldComponentFunction<any>;
      const p: Record<string, any> = {
        ...field.properties,
        focus: !!focus && i === 0,
        hidden:
          hidden || ("hidden" in field.properties && field.properties.hidden),
      };
      if (disabled != null) {
        p.disabled = disabled;
      }

      return <Component key={i} {...p} />;
    }),
  );
}

export function ErrorsSummary<T>({
  errors,
  formName = DEFAULT_FORM_UI_NAME,
  startOpen,
  fixed,
}: {
  errors: FormErrors<T> | undefined;
  formName?: string;
  startOpen?: boolean;
  fixed?: boolean;
}): VNode {
  const { i18n } = useTranslationContext();
  const [opened, setOpened] = useState(startOpen ?? false);

  function Header() {
    return (
      <button
        type="button"
        disabled={fixed}
        data-fixed={!!fixed}
        class="p-2 relative w-full bg-gray-200 flex justify-between text-left data-[fixed=false]:cursor-pointer"
        onClick={() => {
          if (!fixed) {
            setOpened((o) => !o);
          }
        }}
      >
        <span class="px-0">
          <span class="text-base/7 font-semibold text-gray-900">
            <i18n.Translate>List of fields with errors</i18n.Translate>
          </span>
        </span>

        <span class="flex shrink-0 items-center gap-x-4">
          <span class="flex rounded-md shadow-sm border-0 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600"></span>
          {fixed ? (
            <Fragment />
          ) : (
            <span class="rounded-full bg-gray-50 p-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-4 h-4 w-4"
              >
                {opened ? (
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="m19.5 8.25-7.5 7.5-7.5-7.5"
                  />
                ) : (
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="m4.5 15.75 7.5-7.5 7.5 7.5"
                  />
                )}
              </svg>
            </span>
          )}
        </span>
      </button>
    );
  }
  if (!errors || !opened) {
    return (
      <div class="overflow-hidden border border-gray-800  rounded-xl">
        <Header />
      </div>
    );
  }

  return (
    <div class="overflow-hidden border border-gray-800 rounded-xl">
      <Header />

      <div class="border-t border-gray-100">
        <div class="divide-y divide-gray-100">
          {Object.entries(errors).map(([fieldName, handler]) => {
            const errHandler = handler as ErrorAndLabel;
            //FIXME: don't rely on DOM to find the element
            // use preact REF
            return (
              <button
                type="button"
                key={fieldName}
                onClick={() => {
                  const el = document.querySelector(
                    `form[name=${formName}] label[for=${fieldName}]`,
                  ) as HTMLElement;
                  if (el) {
                    el.focus({ preventScroll: true });
                    el.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                      inline: "center",
                    });

                    el.classList.add("animate-pulse");
                    el.classList.add("border-b-2");
                    el.classList.add("border-red-700");
                    setTimeout(() => {
                      el.classList.remove("animate-pulse");
                      el.classList.remove("border-b-2");
                      el.classList.remove("border-red-700");
                    }, 6000);
                  }
                }}
                class="py-2 grid grid-cols-2 gap-4 px-0 odd:bg-white even:bg-gray-100 cursor-pointer"
              >
                {errHandler.section ? (
                  <span class="underline pl-4 text-sm/6 font-medium text-gray-900">
                    {errHandler.section}: {errHandler.label}
                  </span>
                ) : (
                  <span class="underline pl-4 text-sm/6 font-medium text-gray-900">
                    {errHandler.label}
                  </span>
                )}
                <span class="underline flex text-sm/6 text-red-700 mt-0">
                  {errHandler.message}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
